import { DatePicker, Form, FormInstance } from "antd";
import dayjs from 'dayjs';
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useUpdateStatusRequestMoney } from "../../../api/send-money";
import { ReactComponent as CalenderIcon } from '../../../common/images/svg/CalendarIcon.svg';
import { ReactComponent as ClearIcon } from '../../../common/images/svg/XCircle.svg';
import { CustomButton } from "../../../components/button/buttonCustom";
import { ButtonOutlinedCustom } from "../../../components/button/buttonOulined";
import { useNotification } from '../../../components/providers/notification/useNotification';
import {
  clearFieldErrorMessageOnChange
} from "../../../utils/form.constants";
import useResponsive from "../../../utils/hooks/useResponsive";
import { Fiat } from "./form-transfer-props";

interface IFormFutureDateProps {
  detailRequestMoney: any;
  form: FormInstance<any>;
  handleCancel: () => void;
  id: string;
}

const FormFutureDate = ({
  detailRequestMoney,
  form,
  handleCancel,
  id,
}: IFormFutureDateProps) => {
  const datePickerRef = useRef<any>(null)
  const navigate = useNavigate();
  const { isTablet } = useResponsive();
  const { open: openNotification } = useNotification()
  const [searchDate, setSearchDate] = useState<any>(dayjs())
  const { mutate: updatePaymentOrderFutureDate, isLoading: isLoadingPayment } = useUpdateStatusRequestMoney({
    onSuccess: (response: { data: { message: string; data: any } }) => {
      if (openNotification) {
        openNotification({
          key: 'update_future_date_success',
          message: 'Future date updated successfully!',
          type: 'success',
        })
        const isFiat = Fiat.includes(detailRequestMoney?.paymentCurrency);
        navigate(`/history?tab=${isFiat ? 'fiat' : 'crypto'}&orderId=${id}&type=request-money`);
        handleCancel()
      }
    },
    onError: (error: { message: string; reason_code: string }) => {
      if (openNotification) {
        openNotification({
          key: 'update_future_date_failed',
          message: error.message || 'Update future date failed!',
          type: 'error',
        })
      }
    },
  })

  const handleSearchDate = (
    _date: dayjs.Dayjs | null,
    dateString: string | string[]
  ) => {
    setSearchDate(Array.isArray(dateString) ? dateString[0] : dateString)
  }

  const onSubmitPaymentDate = (values: any) => {
    let futurePaymentDate: Date | null = null;

    if (values?.futurePaymentDate) {
      // Check if it's a dayjs instance
      if (dayjs.isDayjs(values.futurePaymentDate)) {
        futurePaymentDate = values.futurePaymentDate.toDate();
      }
      // Check if it's already a Date instance
      else if (values.futurePaymentDate instanceof Date) {
        futurePaymentDate = values.futurePaymentDate;
      }
      // Try to convert string or other formats to Date
      else {
        const date = dayjs(values.futurePaymentDate);
        if (date.isValid()) {
          futurePaymentDate = date.toDate();
        }
      }
    }

    const body = {
      id: id ? Number(id) : null,
      futurePaymentDate,
    }
    updatePaymentOrderFutureDate(body)
  }

  return (
    <Form
      form={form}
      onFinish={onSubmitPaymentDate}
      onValuesChange={() => {
        clearFieldErrorMessageOnChange(form);
      }}
    >
      <div className="form-edit-wallet-content">
        <Form.Item
          style={{ width: '100%' }}
          label='Future date'
          name='futurePaymentDate'
          validateTrigger='onBlur'
          rules={[
            { required: true, message: 'This field is required' },
          ]}>
          <DatePicker
            ref={datePickerRef}
            placeholder='Select date'
            className='custom-input-date send-money'
            format={'DD/MM/YYYY'}
            onChange={(
              value: dayjs.Dayjs | null,
              dateString: string | string[]
            ) => {
              handleSearchDate(value, dateString)
              if (!value) {
                setTimeout(() => {
                  datePickerRef.current?.focus?.()
                })
              }
            }}
            clearIcon={<ClearIcon style={{ height: 12, width: 12 }} />}
            suffixIcon={searchDate ? null : <CalenderIcon />}
            allowClear
            disabledDate={(current) => {
              return current && current < dayjs().startOf('day')
            }}
          />
        </Form.Item>
      </div>
      <div className="form-edit-wallet-footer">
        <ButtonOutlinedCustom
          text={"Cancel"}
          size={isTablet ? "medium" : "large"}
          width={"50%"}
          onClick={handleCancel}
          isLoading={isLoadingPayment}
        />
        <CustomButton
          type="submit"
          text={"Save"}
          size={isTablet ? "medium" : "large"}
          width={"50%"}
          isLoading={isLoadingPayment}
        />
      </div>
    </Form>
  );
};

export default FormFutureDate;
